/**
 * ============================================================================
 * SignAttestationDialog — Phase 6d frontend (Supplier Management).
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: collect signature bundle id(s) + (when DISQUALIFIED) a rationale
 *          ≥200 chars and POST it to the sign endpoint.
 *
 * REGULATORY: VND-007 — APPROVED / CONDITIONAL needs one signer; DISQUALIFIED
 *  needs two distinct signers AND a rationale of at least 200 chars. The
 *  server enforces both. We pre-validate client-side for fast feedback.
 * ============================================================================ */

import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@edms/design-system';
import dialogStyles from '../AuthorWorkbench/NewDocumentForm.module.css';
import styles from './VendorEvaluationScorecard.module.css';
import { useSignAttestationMutation } from './useSignAttestationMutation';
import type { EvaluationClassification } from './useEvaluationQuery';

const RATIONALE_MIN = 200;

export interface SignAttestationDialogProps {
  evaluationId: string;
  classification: EvaluationClassification;
  initialRationale: string;
  onClose: () => void;
  onSigned: () => void;
}

export function SignAttestationDialog({
  evaluationId,
  classification,
  initialRationale,
  onClose,
  onSigned,
}: SignAttestationDialogProps) {
  const { t, i18n } = useTranslation();
  const nf = useMemo(() => new Intl.NumberFormat(i18n.language), [i18n.language]);
  const titleId = useId();
  const descId = useId();
  const firstRef = useRef<HTMLInputElement | null>(null);

  const signM = useSignAttestationMutation();
  const isDisqual = classification === 'DISQUALIFIED';

  const [sigA, setSigA] = useState('');
  const [sigB, setSigB] = useState('');
  const [rationale, setRationale] = useState(initialRationale);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    firstRef.current?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const rationaleLen = rationale.trim().length;
  const sigOk = isDisqual
    ? sigA.trim() !== '' && sigB.trim() !== '' && sigA.trim() !== sigB.trim()
    : sigA.trim() !== '';
  const ratOk = isDisqual ? rationaleLen >= RATIONALE_MIN : true;
  const canSubmit = sigOk && ratOk;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (isDisqual && rationaleLen < RATIONALE_MIN) {
      setError(
        t('supplier.eval.sign.errors.rationaleShort', {
          defaultValue:
            'A disqualified classification requires a rationale of at least {{min}} characters (currently {{len}}).',
          min: nf.format(RATIONALE_MIN),
          len: nf.format(rationaleLen),
        }),
      );
      return;
    }
    if (!sigOk) {
      setError(
        isDisqual
          ? t('supplier.eval.sign.errors.dualSign', {
              defaultValue:
                'Two distinct signature bundle ids are required for a disqualified evaluation.',
            })
          : t('supplier.eval.sign.errors.singleSign', {
              defaultValue: 'A signature bundle id is required.',
            }),
      );
      return;
    }
    try {
      await signM.mutateAsync({
        evaluationId,
        signature_bundle_ids: isDisqual ? [sigA.trim(), sigB.trim()] : [sigA.trim()],
        rationale: isDisqual ? rationale.trim() : undefined,
      });
      onSigned();
    } catch (e: unknown) {
      setError(
        e instanceof Error
          ? e.message
          : t('supplier.eval.sign.errors.serverFailed', {
              defaultValue: 'Server rejected the signature.',
            }),
      );
    }
  }

  return (
    <div
      className={dialogStyles.backdrop}
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={dialogStyles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
      >
        <header className={dialogStyles.header}>
          <h2 id={titleId} className={dialogStyles.title}>
            {t('supplier.eval.sign.title', {
              defaultValue: 'Sign vendor evaluation',
            })}
          </h2>
          <p id={descId} className={dialogStyles.desc}>
            {isDisqual
              ? t('supplier.eval.sign.subtitleDisqual', {
                  defaultValue:
                    'Disqualification requires two distinct signers and a rationale of at least 200 characters (VND-007).',
                })
              : t('supplier.eval.sign.subtitleSingle', {
                  defaultValue:
                    'Confirm the scorecard with your signature bundle id.',
                })}
          </p>
        </header>

        <form className={dialogStyles.form} onSubmit={onSubmit} noValidate>
          {error && (
            <div role="alert" className={dialogStyles.errorBanner}>
              {error}
            </div>
          )}

          <div className={dialogStyles.field}>
            <label htmlFor="sup-sign-a" className={dialogStyles.label}>
              {isDisqual
                ? t('supplier.eval.sign.sigA', {
                    defaultValue: 'Signature bundle id — SIGNER A',
                  })
                : t('supplier.eval.sign.sigSingle', {
                    defaultValue: 'Signature bundle id',
                  })}
              <span className={dialogStyles.required} aria-hidden="true">*</span>
            </label>
            <input
              id="sup-sign-a"
              ref={firstRef}
              className={dialogStyles.input}
              value={sigA}
              onChange={(e) => setSigA(e.target.value)}
              placeholder="sig-…"
              required
            />
          </div>

          {isDisqual && (
            <div className={dialogStyles.field}>
              <label htmlFor="sup-sign-b" className={dialogStyles.label}>
                {t('supplier.eval.sign.sigB', {
                  defaultValue: 'Signature bundle id — SIGNER B',
                })}
                <span className={dialogStyles.required} aria-hidden="true">*</span>
              </label>
              <input
                id="sup-sign-b"
                className={dialogStyles.input}
                value={sigB}
                onChange={(e) => setSigB(e.target.value)}
                placeholder="sig-…"
                required
              />
            </div>
          )}

          {isDisqual && (
            <div className={dialogStyles.field}>
              <label htmlFor="sup-sign-rat" className={dialogStyles.label}>
                {t('supplier.eval.sign.rationale', {
                  defaultValue: 'Disqualification rationale',
                })}
                <span className={dialogStyles.required} aria-hidden="true">*</span>
              </label>
              <textarea
                id="sup-sign-rat"
                className={styles.dialogTextarea}
                value={rationale}
                rows={6}
                onChange={(e) => setRationale(e.target.value)}
                aria-describedby="sup-sign-rat-counter"
              />
              <div
                id="sup-sign-rat-counter"
                className={styles.charCounter}
                aria-live="polite"
              >
                {t('supplier.eval.sign.counter', {
                  defaultValue: '{{len}} / {{min}} characters',
                  len: nf.format(rationaleLen),
                  min: nf.format(RATIONALE_MIN),
                })}
              </div>
            </div>
          )}

          <footer className={dialogStyles.footer}>
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={signM.isPending}
            >
              {t('common.cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button
              type="submit"
              variant="primary"
              isLoading={signM.isPending}
              disabled={!canSubmit}
            >
              {t('supplier.eval.sign.submit', { defaultValue: 'Sign' })}
            </Button>
          </footer>
        </form>
      </div>
    </div>
  );
}
